// Styles
import styled from 'styled-components/macro'

// Core
import React, {Fragment} from 'react'
import {useDispatch, useSelector} from 'react-redux'

// Components, services, etc
import {openModal} from 'actions/modalActions'
import Button from 'components/Button'
import Chip from 'components/Chip'
import {ChipProps} from 'components/Chip/Chip'
import HeaderBreadcrumbs from 'components/HeaderBreadcrumbs'
import LinkButton from 'components/LinkButton'
import UserMenu from 'components/UserMenu'
import {SUPPORT_CENTER_URL} from 'constants/appConstants'
import {LOG_IN_SIGN_UP_MODAL} from 'constants/modalsConstants'
import {creditsSelector, isUserAuthenticatedSelector, userSelector} from 'selectors'
import {isInvoicePaySelector} from 'selectors/customerSelectors'
import {walletSelector} from 'selectors/walletSelectors'
import type {AppDispatch} from 'services/store'
import type {GetCreditsResponse} from 'types/orderTypes'

type Props = {
  className?: string
}

type LoggedInProps = {
  userInfo: GetCreditsResponse
  stripeCustomerExists?: boolean
  isInvoicePay: boolean
}

type LoggedOutProps = {
  dispatch: AppDispatch
}

const determineChipColor = (credits: number) => {
  let color: ChipProps['color']
  switch (true) {
    case credits >= 5:
      color = 'emerald'
      break
    case credits < 5 && credits > 0:
      color = 'citrine'
      break
    default:
      color = 'ruby'
  }
  return color
}

const AppHeader = ({className}: Props) => {
  const dispatch = useDispatch()
  const isUserAuthenticated = useSelector(isUserAuthenticatedSelector)
  const userData = useSelector(userSelector)
  const creditsInfo = useSelector(creditsSelector)
  const {stripeCustomerExists} = useSelector(walletSelector)
  const isInvoicePay = useSelector(isInvoicePaySelector)

  const userInfo = {...userData, ...creditsInfo}

  return (
    <AppHeader.Styled className={className}>
      <HeaderBreadcrumbs />
      <div className='header-right'>
        <LinkButton variant='tertiary' title='Support' href={SUPPORT_CENTER_URL} target='_blank' />
        {isUserAuthenticated ? (
          <AppHeader.LoggedIn
            userInfo={userInfo}
            stripeCustomerExists={stripeCustomerExists}
            isInvoicePay={isInvoicePay}
          />
        ) : (
          <AppHeader.LoggedOut dispatch={dispatch} />
        )}
      </div>
    </AppHeader.Styled>
  )
}

AppHeader.LoggedIn = ({userInfo, stripeCustomerExists, isInvoicePay}: LoggedInProps) => {
  const {creditsRemaining} = userInfo
  const showChip =
    !isInvoicePay &&
    creditsRemaining &&
    (creditsRemaining > 0 || (creditsRemaining === 0 && !stripeCustomerExists))
  return (
    <Fragment>
      {showChip ? (
        <Chip
          color={determineChipColor(creditsRemaining)}
          label={`${creditsRemaining} ClearProp Credits Left`}
          sqaPrefix='credits'
        />
      ) : null}
      <UserMenu />
    </Fragment>
  )
}

AppHeader.LoggedOut = ({dispatch}: LoggedOutProps) => {
  const handleOpenModal = (mode: 'logIn' | 'signUp') => () => {
    dispatch(
      openModal({
        modalType: LOG_IN_SIGN_UP_MODAL,
        mode,
        mixpanelEventData: {
          initiatedFrom: 'App Header'
        }
      })
    )
  }

  return (
    <Fragment>
      <Button variant='tertiary' title='Log In' onClick={handleOpenModal('logIn')} />
      <Button variant='primary' title='Sign Up' onClick={handleOpenModal('signUp')} />
    </Fragment>
  )
}

AppHeader.Styled = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: row;
  z-index: 3;
  background: ${({theme}) => theme.colors.grayscale.white};
  height: 48px;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.08), 0px 1px 0px rgba(0, 0, 0, 0.04);
  justify-content: space-between;
  align-items: center;
  padding: 0 40px;
  .header-right {
    display: flex;
    justify-content: flex-end;
    align-items: center;

    .tertiary-button {
      color: ${({theme}) => theme.colors.grayscale.black};
    }
    button,
    .chip {
      margin-left: 24px;
    }
    // Support Link button needs 6px less due to Address search padding-right
    .tertiary-button.link-button {
      margin-left: 18px;
    }
  }
`

export default AppHeader
